import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Height that makes an element fill the rest of the viewport.
 *
 * The assistant thread needs its own scroll region, which means a real pixel
 * height. A CSS `calc(100dvh - <constant>)` can't do it honestly: the constant
 * has to guess the header, the page title, the impersonation banner and the
 * mobile tab bar, and it's wrong the moment any of them changes — and `dvh`
 * doesn't shrink for the on-screen keyboard, so on a phone the composer ends up
 * under it.
 *
 * So measure instead: the element's own distance from the top of the viewport,
 * minus whatever obstructs the bottom. `visualViewport` is the keyboard-aware
 * height where it exists (every browser we target except very old ones), and it
 * re-measures on resize, scroll-of-the-visual-viewport, and orientation change.
 *
 * The mobile tab bar is measured from the DOM (`[data-bottom-bar]`) rather than
 * hardcoded, so it can't drift out of sync with the layout — and it's skipped
 * automatically on desktop, where that bar is `md:hidden`.
 *
 * @param ref  the element to size
 * @param gap  px of breathing room to leave below the element
 */
export function useFillHeight(ref: RefObject<HTMLElement | null>, gap = 0): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const viewport = window.visualViewport;
      const available = viewport?.height ?? window.innerHeight;
      // Element top is relative to the layout viewport; visualViewport.offsetTop
      // is how far the visual viewport has been pushed down (keyboard/zoom).
      const top = el.getBoundingClientRect().top - (viewport?.offsetTop ?? 0);
      // Fixed bottom chrome, if it's actually rendered (it is `md:hidden`, and
      // an element hidden by `display:none` has no offsetParent).
      const bar = document.querySelector<HTMLElement>('[data-bottom-bar]');
      const barHeight = bar && bar.offsetParent !== null ? bar.offsetHeight : 0;
      // A floor keeps the pane usable rather than collapsing to nothing if the
      // measurement lands mid-layout.
      setHeight(Math.max(240, Math.round(available - top - barHeight - gap)));
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    // Header/banner height can change without a viewport event (impersonation
    // banner appearing, title wrapping) — observe the element's own position.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [ref, gap]);

  return height;
}
